import { useLayoutEffect, useState, type RefObject } from 'react';

const DEBOUNCE_MS = 100;

/**
 * Pixel width of the observed element. The first measurement is synchronous so the first
 * paint uses the real width. Later resize events are debounced. `0` means not yet measured.
 */
export function useContainerWidth(ref: RefObject<HTMLElement | null>): number {
  const [width, setWidth] = useState(0);

  useLayoutEffect(() => {
    const element = ref.current;
    if (element === null) return;
    const initial = Math.round(element.getBoundingClientRect().width);
    if (initial > 0) setWidth(initial);
    if (typeof ResizeObserver === 'undefined') return;
    let timer: number | null = null;
    const observer = new ResizeObserver((entries) => {
      const next = Math.round(entries[0]?.contentRect.width ?? 0);
      if (timer !== null) window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        timer = null;
        setWidth((prev) => (prev === next ? prev : next));
      }, DEBOUNCE_MS);
    });
    observer.observe(element);
    return () => {
      observer.disconnect();
      if (timer !== null) window.clearTimeout(timer);
    };
  }, [ref]);

  return width;
}
